import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.14), transparent 58%), radial-gradient(ellipse at 85% 20%, rgba(249,115,22,0.10), transparent 55%)' }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <div className="eyebrow">Our Story</div>
            <h1 className="mt-6 h-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              About Us
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 font-body font-light max-w-2xl">
              Florida's first AI-native marketing agency helping local businesses win the next era of customer acquisition.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto mb-20">
            <h2 className="text-4xl font-bold font-display text-navy-900 mb-6">
              Our Mission
            </h2>
            <p className="text-base text-navy-600 leading-relaxed mb-6">
              We exist to help Florida businesses thrive in the AI era. While others are still figuring out what AI means for marketing, we're already implementing it—helping our clients get discovered, recommended, and booked through AI-powered channels.
            </p>
            <p className="text-base text-navy-600 leading-relaxed">
              We believe the businesses that win in the next decade will be those that embrace AI early. Not just as a buzzword, but as a fundamental shift in how customers discover and choose service providers.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-navy-50 rounded-2xl p-8 border-2 border-navy-100">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-xl mb-6">
                <i className="ri-map-pin-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-4">
                Florida-First
              </h3>
              <p className="text-navy-600 leading-relaxed text-sm">
                We understand Florida's unique market dynamics, seasonal patterns, and local competition. Our strategies are built for Florida businesses, not generic templates.
              </p>
            </div>

            <div className="bg-navy-50 rounded-2xl p-8 border-2 border-navy-100">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-xl mb-6">
                <i className="ri-robot-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-4">
                AI-Native
              </h3>
              <p className="text-navy-600 leading-relaxed text-sm">
                We don't just talk about AI—we implement it. From ChatGPT ad campaigns to voice AI agents, we build and manage your entire AI marketing stack.
              </p>
            </div>

            <div className="bg-navy-50 rounded-2xl p-8 border-2 border-navy-100">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-xl mb-6">
                <i className="ri-focus-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-4">
                Results-Driven
              </h3>
              <p className="text-navy-600 leading-relaxed text-sm">
                We measure what matters: AI visibility, conversational ad performance, voice agent conversions, and ultimately—revenue growth for your business.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white rounded-2xl p-12 mb-20">
            <h2 className="text-3xl font-bold font-display mb-8 text-center">
              Why We Started Florida AI Marketing Experts
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <p className="text-base text-navy-100 leading-relaxed">
                We saw a massive gap in the market. AI assistants like ChatGPT were changing how people find businesses, but most marketing agencies were still focused on traditional SEO and Google Ads.
              </p>
              <p className="text-base text-navy-100 leading-relaxed">
                Florida businesses were asking questions like "How do I show up in ChatGPT?" and "What are conversational ads?" but getting no clear answers. Meanwhile, early movers in other markets were already capturing this opportunity.
              </p>
              <p className="text-base text-navy-100 leading-relaxed">
                We decided to become Florida's AI marketing specialists—not just consultants who talk about it, but practitioners who implement it. Today, we help Florida businesses prepare for ChatGPT ads, optimize for AI search visibility, and convert leads instantly with voice AI agents.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mb-20">
            <h2 className="text-3xl font-bold font-display text-navy-900 mb-8 text-center">
              Our Approach
            </h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                  <span className="text-xl font-bold text-teal-600">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold font-display text-navy-900 mb-2">Assessment & Strategy</h3>
                  <p className="text-navy-600 leading-relaxed text-sm">
                    We start by understanding where you are today—your AI visibility, competitive positioning, and conversion funnel. Then we build a custom strategy for your business and goals.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                  <span className="text-xl font-bold text-teal-600">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold font-display text-navy-900 mb-2">Implementation</h3>
                  <p className="text-navy-600 leading-relaxed text-sm">
                    We don't just hand you a plan—we execute it. Our team builds your GEO content, sets up voice AI agents, prepares ChatGPT ad campaigns, and integrates everything with your existing systems.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                  <span className="text-xl font-bold text-teal-600">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold font-display text-navy-900 mb-2">Optimization & Reporting</h3>
                  <p className="text-navy-600 leading-relaxed text-sm">
                    We continuously monitor performance, test improvements, and report results. You see exactly what's working and why, with transparent metrics and regular strategy calls.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-navy-50 rounded-2xl p-12 border-2 border-navy-100 text-center">
            <h2 className="text-3xl font-bold font-display text-navy-900 mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-base text-navy-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Let's talk about how AI marketing can help your Florida business grow. Start with a free AI Assessment.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/audit"
                className="btn-primary px-8 py-4 whitespace-nowrap cursor-pointer"
              >
                Get Free AI Assessment
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-white border-2 border-navy-200 text-navy-900 font-bold rounded-lg hover:border-teal-500 transition-all whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
